import { Typography } from 'antd';
import React, { ReactElement, useEffect } from 'react';
import { Dispatch } from 'redux';
import { useDispatch, useSelector } from 'react-redux';
import Layout from './Layout';
import { incomeTrustInterestRatesListAction } from '~Investments/actions';
import { incomeTrustInterestRatesSelector } from '~Investments/selectors';
import IIncomeTrustInterestRate from '~Api/IncomeTrust/IIncomeTrustInterestRate';
import { IDictionary } from '~utilities/IDictionary';
import Table, { ColumnsType } from 'antd/lib/table';
import _ from 'lodash';
import IncomeTrustClassTypeEnum from '~Api/IncomeTrust/IncomeTrustClassTypeEnum';
import classTypeLabels from '~Api/IncomeTrust/classTypeLabels';
import dayjs from 'dayjs';
import { percentageFormatter } from '~utilities/formatters';

interface IInterestRatesByDate {
    [IncomeTrustClassTypeEnum.ClassC]: number;
    [IncomeTrustClassTypeEnum.ClassD]: number;
    effectiveDate: string;
}

export default function InterestRates(): ReactElement {
    const incomeTrustInterestRates: IDictionary<IIncomeTrustInterestRate> = useSelector(incomeTrustInterestRatesSelector);

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!incomeTrustInterestRates) {
            dispatch(incomeTrustInterestRatesListAction());
        }
    }, [
        dispatch,
        incomeTrustInterestRates,
    ]);

    const interestRatesByDates: IDictionary<IInterestRatesByDate> = {};

    _.each(incomeTrustInterestRates, (incomeTrustInterestRate: IIncomeTrustInterestRate) => {
        if (!interestRatesByDates[incomeTrustInterestRate.effectiveDate]) {
            interestRatesByDates[incomeTrustInterestRate.effectiveDate] = {
                effectiveDate: incomeTrustInterestRate.effectiveDate,
                [IncomeTrustClassTypeEnum.ClassC]: null,
                [IncomeTrustClassTypeEnum.ClassD]: null,
            };
        }
        interestRatesByDates[incomeTrustInterestRate.effectiveDate][incomeTrustInterestRate.classType] = incomeTrustInterestRate.interestRate;
    });

    const columns: ColumnsType<IInterestRatesByDate> = [
        {
            dataIndex: 'effectiveDate',
            defaultSortOrder: 'descend',
            render: (effectiveDate: string) => dayjs(effectiveDate).format('DD/MM/YYYY'),
            sorter: (a: IInterestRatesByDate, b: IInterestRatesByDate) => dayjs(a.effectiveDate) > dayjs(b.effectiveDate) ? 1 : -1,
            title: 'Effective Date',
        },
        {
            dataIndex: IncomeTrustClassTypeEnum.ClassC,
            render: (interestRate: number) => interestRate && percentageFormatter.format(interestRate / 100),
            title: classTypeLabels[IncomeTrustClassTypeEnum.ClassC],
        },
        {
            dataIndex: IncomeTrustClassTypeEnum.ClassD,
            render: (interestRate: number) => interestRate && percentageFormatter.format(interestRate / 100),
            title: classTypeLabels[IncomeTrustClassTypeEnum.ClassD],
        },
    ];

    return (
        <Layout section='interest-rates'>
            <Typography.Title level={2}>Interest Rates</Typography.Title>
            <Table
                columns={columns}
                dataSource={_.values(interestRatesByDates)}
                loading={!incomeTrustInterestRates}
                pagination={false}
                rowKey='effectiveDate'
                size='middle'
            />
        </Layout>
    );

}
